/**
 * Minimal JWT reading.
 *
 * This **decodes and never verifies**. Signature verification belongs to the
 * APIs that consume the token; a browser cannot do it meaningfully without the
 * signing keys. We read the payload for exactly two reasons: to know when to
 * renew (`exp`), and to know who the token is for (`sub`) so the identity fence
 * has something to compare. Treat every value returned here as a hint, never as
 * an authorisation decision.
 */

export type JwtClaims = Readonly<Record<string, unknown>>;

const PART_COUNT = 3;

/**
 * Turns a binary string into UTF-8 via percent-decoding.
 *
 * Deliberately avoids `TextDecoder`: it is missing from jsdom and from some
 * mobile webviews, and this library has to run wherever the apps that embed it
 * run. `atob` is the only platform primitive assumed here.
 */
function utf8FromBinary(binary: string): string {
  let percentEncoded = '';
  for (let index = 0; index < binary.length; index += 1) {
    const code = binary.charCodeAt(index).toString(16).padStart(2, '0');
    percentEncoded += `%${code}`;
  }
  return decodeURIComponent(percentEncoded);
}

/** Decodes a base64url segment to a UTF-8 string. */
function decodeSegment(segment: string): string {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));
  return utf8FromBinary(binary);
}

/**
 * Returns the token's claims, or null when the input is not a readable JWT.
 * Never throws — a malformed token is an expected input here, not an error.
 */
export function readJwtClaims(token: string): JwtClaims | null {
  const parts = token.split('.');
  if (parts.length !== PART_COUNT) return null;

  try {
    const parsed: unknown = JSON.parse(decodeSegment(parts[1]));
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as JwtClaims;
  } catch {
    return null;
  }
}

function readStringClaim(claims: JwtClaims, name: string): string | undefined {
  const value = claims[name];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readNumericClaim(claims: JwtClaims, name: string): number | undefined {
  const value = claims[name];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export type JwtSummary = {
  /** Epoch seconds. */
  expiresAt?: number;
  subject?: string;
  issuer?: string;
  claims: JwtClaims;
};

/** Pulls the claims we act on out of a token, or null when it is unreadable. */
export function summariseJwt(token: string): JwtSummary | null {
  const claims = readJwtClaims(token);
  if (!claims) return null;

  return {
    expiresAt: readNumericClaim(claims, 'exp'),
    subject: readStringClaim(claims, 'sub'),
    issuer: readStringClaim(claims, 'iss'),
    claims,
  };
}
